"use client";

import {
  AuctionState,
  AuctionType,
  BidType,
  BiddingSchedule,
  BiddingScheduleRange,
  HighestBid,
  Lot,
  Role,
} from "@prisma/client";
import { Flex } from "@radix-ui/themes";
import { useSession } from "next-auth/react";
import { useParams }  from "next/navigation";
import React, { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { calculateBidIncrement as calculateIncrement, cn, fetcher } from "@/lib/utils";
import axios, { AxiosError, HttpStatusCode } from "axios";
import useCustomToast from "@/lib/hooks/useCustomToast";
import BiddingCardWinnerMessage from "../lots/[lotId]/CardWidgets/BiddingCardWinnerMessage";
import BiddingCardInput from "../lots/[lotId]/CardWidgets/BiddingCardInput";
import BID_ACTIONS from "../lots/[lotId]/BidActions";
import LoginPopup from "@/app/components/LoginPopup";
import LotLogInBanner from "@/app/components/LotLogInBanner";
import BidPopup from "@/app/components/BidPopup";
import { Separator } from "@/app/components/ui/separator";
import LotBidHistory, {
  LOT_BID_HISTORY_MAX_BID_QUERY_KEY,
  LOT_BID_HISTORY_QUERY_KEY,
} from "../lots/[lotId]/history/LotBidHistory";
import useSWR, { useSWRConfig } from "swr"
import { SavePaymentSelectionHandle } from "@/app/components/creditCardSelect/PaymentMethodSelect";
import { respectsBiddingSchedule } from "@/lib/bidding/util";
import { useSharedSubscription } from "@/app/hooks/useSharedNATS";
import { FLASH_DURATION } from "@/lib/constants";
import CurrentBid from "../lots/[lotId]/CurrentBid";
import OtherBids from "../lots/[lotId]/OtherBids";


// Basically bidding card, but without confirmations, 
// and with way more quick bid options.

export interface BidInfo {
  amount: number;
  bidType: BidType;
  flash?: boolean;
}

interface Props {
  biddingSchedule: BiddingSchedule & {
    BiddingScheduleRange: BiddingScheduleRange[];
  };
  lot: Lot;
  expired: boolean;
  currentBid: HighestBid | null;
  auctionStart: Date;
  auctionEnd: Date;
  auctionState: AuctionState;
  auctionType: AuctionType;
}

const NOTIFICATION_DURATION = 3 * 1000;
const DB_POLL_DURATION = 5 * 1000;
const NO_CURRENT_HIGHEST_BID = 0;

const VALID_BID_STATES: AuctionState[] = [
  AuctionState.AUCTION,
  AuctionState.SOFT_CLOSING,
];

const INVALID_BID_STATES: AuctionState[] = [
  AuctionState.PREVIEW,
  AuctionState.CREATING,
  AuctionState.PAUSED,
  AuctionState.POST_AUCTION,
  AuctionState.MANUALLY_ENDED,
];

interface BidState {
  initialized: boolean;
  currentHighestBid: number;
  currentWinner: string | undefined;
  isCurrentUserWinner: boolean;
  flash: boolean;
}

const FloorBidRecorderBiddingCard = ({
  biddingSchedule,
  lot,
  expired,
  currentBid: initialBid,
  auctionStart,
  auctionEnd,
  auctionState,
  auctionType,
}: Props) => {
  const { auctionId } = useParams();
  const lotId = lot.id.toString();

  const { mutate } = useSWRConfig();

  const calculateBidMin = useCallback((currentBid: number | undefined) => {
    if (currentBid) {
      return currentBid + calculateIncrement({
        currentBid,
        ranges: biddingSchedule.BiddingScheduleRange,
      });
    }
    return lot.startBid!;
  }, [biddingSchedule.BiddingScheduleRange, lot.startBid]);

  const calculateBidIncrement = useCallback((currentBid: number | undefined) => {
    return calculateIncrement({
      currentBid: currentBid || lot.startBid!,
      ranges: biddingSchedule.BiddingScheduleRange,
    });
  }, [biddingSchedule.BiddingScheduleRange, lot.startBid]);

  const {
    data: currentHighestBid,
    mutate: mutateCurrentHighestBid,
  } = useSWR(
    `/api/auctions/${auctionId}/lots/${lotId}/highestBid`,
    fetcher,
    {
      fallbackData: initialBid,
      revalidateOnFocus: true,
      revalidateOnMount: true,
    }
  );

  const [bidMin, setBidMin] = useState(() => calculateBidMin(currentHighestBid?.amount));
  const [bidIncrement, setBidIncrement] = useState(() => calculateBidIncrement(currentHighestBid?.amount));

  useEffect(() => {
    setBidMin(calculateBidMin(currentHighestBid?.amount));
    setBidIncrement(calculateBidIncrement(currentHighestBid?.amount));
  }, [currentHighestBid, calculateBidMin, calculateBidIncrement]);

  const initialState: BidState = {
    initialized: false,
    currentHighestBid: initialBid?.amount ?? NO_CURRENT_HIGHEST_BID,
    currentWinner: initialBid?.userId,
    isCurrentUserWinner: false,
    flash: false,
  };

  const sessionRef = useRef<string | null | undefined>(null);
  const { status, data: session }: any = useSession();
  const [displayLoginPopup, setDisplayLoginPopup] = useState(false);
  const [displayBidPopup, setDisplayBidPopup] = useState(false);
  const [state, dispatch] = useReducer(reducer, initialState);
  const { toast, toastError } = useCustomToast();
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [disableBid, setDisableBid] = useState(
    Object.values(INVALID_BID_STATES).includes(auctionState)
  );
  const [bidInfo, setBidInfo] = useState<BidInfo>({
    amount: 0,
    bidType: BidType.STANDARD,
  });

  // Track whether the current user has ever placed a bid on this lot.
  // This is fetched once per mount and treated as sticky true.
  const [hasUserBids, setHasUserBids] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchHasUserBids = async () => {
      if (!auctionId || !lotId) return;
      try {
        const res = await fetch(`/api/auctions/${auctionId}/lots/${lotId}/has-bid`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data && typeof data === 'object') {
          setHasUserBids(!!data.hasBids);
        }
      } catch { /* noop */ }
    };
    fetchHasUserBids();
    return () => { cancelled = true; };
  }, [auctionId, lotId]);

  const stateRef = useRef<BidState>(initialState);
  const savePaymentSelectionRef = useRef<SavePaymentSelectionHandle>(null);

  function reducer(state: BidState, action: { type: string; payload?: any }) {
    switch (action.type) {
      case BID_ACTIONS.INITIALIZE:
        if (!action.payload.userId || state.initialized) return state;
        return {
          ...state,
          initialized: true,
          isCurrentUserWinner:
            initialBid?.userId === action.payload.userId,
        };

      case BID_ACTIONS.UPDATE_HIGHEST_BID:
        const { amount, userId } = action.payload;
        if (!amount || !userId) return state;

        return {
          ...state,
          currentHighestBid: amount,
          currentWinner: userId,
          isCurrentUserWinner: userId === session?.userId,
          flash: true,
        };

      case BID_ACTIONS.UNFLASH:
        return { ...state, flash: false };
    }
    throw Error("Unknown action.");
  }

  useEffect(() => {
    setDisableBid(Object.values(INVALID_BID_STATES).includes(auctionState));
  }, [auctionState, auctionStart, auctionEnd]);

  useEffect(() => {
    dispatch({ type: BID_ACTIONS.INITIALIZE, payload: { userId: session?.userId } });
    sessionRef.current = session?.userId;
    // console.log("sessionRef.current", sessionRef.current);
  }, [session]);

  // Solves stale closure problem for onNewHighestBid.
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const onNewHighestBid = useCallback((payload: any) => {
    // Ignore bids that are less than or equal to the current highest bid.
    if (payload.amount <= stateRef.current?.currentHighestBid) return;

    mutateCurrentHighestBid(payload as HighestBid);
    mutate([LOT_BID_HISTORY_QUERY_KEY, auctionId, lotId]);
    mutate([LOT_BID_HISTORY_MAX_BID_QUERY_KEY, auctionId, lotId]);
    if (
      stateRef.current?.isCurrentUserWinner &&
      payload?.userId !== stateRef.current?.currentWinner
    ) {
      toast({
        title: "You've been outbid.",
        description: `Someone has outbid you.`,
        duration: NOTIFICATION_DURATION,
      });
    } else if (
      payload?.userId === sessionRef.current && 
      payload?.isAuto === true
    ) {
      toast({
        title: "Your max bid triggered a bid.",
        description: `The system bid $${payload.amount} on your behalf.`,
        duration: NOTIFICATION_DURATION,
      });
    }

    dispatch({
      type: BID_ACTIONS.UPDATE_HIGHEST_BID,
      payload: {
        amount: payload.amount,
        userId: payload.userId,
      },
    });

    const newBidIncrement = calculateIncrement({
      currentBid: payload.amount,
      ranges: biddingSchedule.BiddingScheduleRange,
    });

    setBidIncrement(newBidIncrement);
    setBidMin(payload.amount + newBidIncrement);
  }, [biddingSchedule.BiddingScheduleRange, mutateCurrentHighestBid, toast, auctionId, lotId, mutate]);

  useSharedSubscription({
    topic: `postgres.${process.env.NEXT_PUBLIC_DB_SCHEMA!}.Lot${lotId}.HighestBid`,
    onMessage: onNewHighestBid,
  });

  const placeBid = async ({ amount, bidType }: BidInfo) => {
    if (session?.role === Role.ADMIN) {
      alert("Admins are not allowed to bid.")
      return;
    }

    if (disableBid) {
      toastError({
        title: "Auction unavailable for bidding",
        description:
          "You are currently unable to place a bid because the auction is not active.",
      });
      return;
    }
    // Fetch the current time and compare it directly to lot end Time

    if (expired) {
      toastError({
        title: "The lot has closed.",
        description:
          "You are currently unable to place a bid because this lot has closed.",
      });
      return;
    }

    if (status === "unauthenticated" || !session) {
      setDisplayLoginPopup(true);
      return;
    }

    setIsSubmitting(true);
    if (isNaN(amount)) {
      setError("Please enter a valid bid.");
      setIsSubmitting(false);
      return;
    } else if (amount < bidMin) {
      setError(`Please bid at least $${bidMin} to proceed.`);
      setIsSubmitting(false);
      return;
    } else if (Math.floor(amount) !== amount) {
      setError(`Please enter a full dollar amount to proceed.`);
      setIsSubmitting(false);
      return;
    } else if (bidType === BidType.STANDARD && !respectsBiddingSchedule(amount, biddingSchedule)) {
      setError(`Please enter a bid that respects the bid increment schedule.`);
      setIsSubmitting(false);
      return
    } else {
      setError("");
    }

    // Bid is valid. Confirm it.
    if (status === "authenticated" && session?.role === Role.BUYER) {
    //   setDisplayBidPopup(true);
      setBidInfo({ amount: amount, bidType: bidType });
      await confirmBid({ amount, bidType });
    }

    setIsSubmitting(false);
  };

  const confirmBid = async ({ amount, bidType }: BidInfo) => {
    // Optimistically update the UI

    const previousHighestBid = JSON.parse(JSON.stringify(currentHighestBid));
    if (bidType === BidType.STANDARD) {
      await mutateCurrentHighestBid(
        { amount, userId: sessionRef.current },
        false
      );
    }

    try {
      const response = await axios.post(`/api/auctions/${auctionId}/lots/${lotId}/bids`, {
        session,
        amount,
        bidType,
      });

      savePaymentSelectionRef.current?.savePaymentSelection();

      // Don't optimistically update these, because we can't revert them on error.
      mutate([LOT_BID_HISTORY_QUERY_KEY, auctionId, lotId]);

      if (bidType === BidType.MAX) {
        mutate([LOT_BID_HISTORY_MAX_BID_QUERY_KEY, auctionId, lotId]);
      }

      if (response.data?.userId !== sessionRef.current && response.data?.amount === amount) {
        toast({
          title: "Auto-bid beat your bid!",
          description: "Your bid was placed, but someone set a max bid at the same amount or higher before you. To take the lead, please place a higher bid.",
          duration: 10000
        });
      } else if (response.data?.userId === sessionRef.current) {
        toast({
          title: "Bid successful!",
          description: `Your ${bidType === BidType.MAX ? "max " : ""}bid of $${amount} was successful.`,
        });
        // Mark that the user has bids on this lot from now on
        if (!hasUserBids) setHasUserBids(true);
      }

      // Subscribe to the lot
      await fetch(`/api/account/subscriptions/subscribe`, {
        method: "POST",
        body: JSON.stringify({
          subscribe: true,
          lotId: parseInt(lotId as string),
          userId: sessionRef.current,
          auctionId: parseInt(auctionId as string)
        })
      })

      mutate(`/api/account/subscriptions/${sessionRef.current}/${lotId}`, {
        isSubscribed: true
      })
      mutate(`/api/auctions/${auctionId}/lots/${lotId}/followers`);
    } catch (error) {
      // console.log('error', error)

      // Revert optimistic updates on error
      mutate([LOT_BID_HISTORY_QUERY_KEY, auctionId, lotId]);
      if (bidType === BidType.MAX) {
        mutate([LOT_BID_HISTORY_MAX_BID_QUERY_KEY, auctionId, lotId]);
      }

      if (error instanceof AxiosError) {
        console.error(JSON.stringify(error.response, null, 2));
        if (error?.response?.data?.error === "flagged") {
          toastError({
            title: "Bid Failed",
            description:
              "Your account has been suspended for suspicious activity. As a result, your bid has been rejected.",
          });
        } else {
          toastError({
            title: "Bidding Error",
            description: error?.response?.data?.message,
          });
        }

        if (error.status !== HttpStatusCode.Conflict) {
          mutateCurrentHighestBid(previousHighestBid, false);
        } else {
          mutateCurrentHighestBid();
        }
      } else {
        throw error;
      }
    }
  };

  const focusPlaceholderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    !displayBidPopup && focusPlaceholderRef.current?.focus();
  }, [displayBidPopup]);

  const isCurrentUserHighestBidder = useCallback(() => {
    return currentHighestBid && // There has to be a highest bid
      sessionRef?.current && // There has to be a user session
      currentHighestBid?.userId === sessionRef.current; // The highest bidder has to be the current user
  }, [currentHighestBid, sessionRef]);

  return (
    <>
      <Flex className={cn("lot-page-card", "max-sm:p-2 max-w-[500px]")} ref={focusPlaceholderRef}>
        <Flex direction="column" className="w-full m-2.5" gap="5">
          <Flex className="w-full" align="start">
            <CurrentBid
              auctionState={auctionState}
              startBid={lot.startBid!}
              currentBid={currentHighestBid?.amount || lot.startBid!}
              started={!!currentHighestBid?.userId}
              expired={expired}
              flash={state.flash}
              dispatch={dispatch}
            />
          </Flex>
          <BiddingCardWinnerMessage
            visible={isCurrentUserHighestBidder()}
            expired={expired}
            hasUserBids={hasUserBids}
          />
          <Flex direction="column" className="my-2" gap="3" align="center">
            <Flex className={cn("w-[90%]", auctionType === AuctionType.HYBRID ? "w-full space-y-2 flex-col" : "")}>
              <OtherBids
                hasBids={!!currentHighestBid?.amount}
                currentBid={currentHighestBid?.amount || lot.startBid!}
                biddingSchedule={biddingSchedule}
                isCurrentUserWinner={isCurrentUserHighestBidder()}
                disabled={isSubmitting || disableBid}
                setIsDisabled={setIsSubmitting}
                expired={expired}
                onClick={(amount: number) => {
                  placeBid({ amount, bidType: auctionType === AuctionType.HYBRID ? BidType.STANDARD : BidType.MAX });
                }}
                auctionType={auctionType}
                floorBidder={true}
              />
              {auctionType === AuctionType.HYBRID && (
                <Separator className="my-4" />
              )}
            </Flex>
            <BiddingCardInput
              auctionState={auctionState}
              placeBid={placeBid}
              minBid={bidMin}
              error={error}
              isCurrentUserWinner={isCurrentUserHighestBidder()}
              expired={expired}
              isSubmitting={isSubmitting}
              disableBid={disableBid}
              disableMaxBid={auctionType === AuctionType.HYBRID}
              biddingSchedule={biddingSchedule}
            />
            <Separator className="my-5" />
            <LotBidHistory
              key={lotId}
              auctionState={auctionState}
              auctionId={auctionId as string}
              lotId={lotId.toString()}
            />
          </Flex>
        </Flex>
      </Flex>
    </>
  );
};

export default FloorBidRecorderBiddingCard;
